import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const ReviewsSection = () => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const reviews = [
    {
      name: "Sandeep V",
      date: "Jan 2024",
      rating: 5,
      text: "Dr. Suresh Babu is exceptionally skilled. He performed a complex orthopedic surgery on my dog and the recovery was amazing. Highly recommend this clinic.",
      source: "Google"
    },
    {
      name: "Priyanka R",
      date: "Nov 2023",
      rating: 5,
      text: "Very professional and hygienic clinic. They have all lab facilities inside. The staff is caring and the response is very quick.",
      source: "Google"
    },
    {
      name: "Rahul G",
      date: "Feb 2024",
      rating: 4,
      text: "Experienced doctor. My pet got cured within 2 days of treatment. Highly recommend for any emergency situations.",
      source: "Google"
    },
    {
      name: "Anil Kumar",
      date: "Mar 2024",
      rating: 5,
      text: "Best boarding and surgical facility for dogs. Dr. Suresh treats every pet like his own. The clinic is very spacious and clean.",
      source: "Google"
    },
    {
      name: "Madhavi L",
      date: "Dec 2023",
      rating: 5,
      text: "I was worried about my pet's skin infection for months. After visiting Dr. Suresh's, it cleared up in two weeks. Amazing care.",
      source: "Google"
    },
    {
      name: "Vikas T",
      date: "Jan 2024",
      rating: 5,
      text: "Highly equipped OT and diagnostic lab. The blood test results were very accurate and explained clearly by the doctor.",
      source: "Google"
    }
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' 
        ? scrollLeft - clientWidth * 0.8 
        : scrollLeft + clientWidth * 0.8;
      
      scrollRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.review-reveal', 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="bg-white relative overflow-hidden" id="reviews">
      <div className="absolute top-0 right-0 w-[60vw] h-[60vw] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="section-container">
        <div className="flex flex-col gap-16 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end gap-12">
            <div className="space-y-6 max-w-2xl">
              <div className="flex items-center gap-4">
                 <span className="w-12 h-px bg-primary/30"></span>
                 <span className="review-reveal text-primary font-bold tracking-[0.2em] uppercase text-[10px] md:text-[11px] block">Testimonials</span>
              </div>
              <h2 className="review-reveal text-navy font-display font-bold leading-tight">
                 Why Pet Parents Choose <br /> <span className="text-primary italic font-normal">Dr. Suresh Pet Care.</span>
              </h2>
              <p className="review-reveal text-luxury text-xl max-w-xl">
                 Providing a clinical environment built on trust, technological precision, and 24/7 dedication to your pet's wellbeing.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-8 w-full md:w-auto">
              <div className="review-reveal flex items-end gap-6 p-8 bg-secondary/30 rounded-[2.5rem] border border-white/50 shadow-glass-light backdrop-blur-md">
                 <div className="space-y-1 text-center sm:text-left">
                    <p className="text-6xl font-black font-display leading-none text-navy">4.7</p>
                    <div className="flex gap-1 text-primary justify-center sm:justify-start">
                       {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                    </div>
                 </div>
                 <p className="text-[10px] font-black uppercase tracking-[0.2em] text-navy/40 pb-1 leading-tight text-center sm:text-left">Verified <br/> Google Reviews</p>
              </div>

              <div className="review-reveal flex gap-4">
                 <button 
                  onClick={() => scroll('left')}
                  disabled={!canScrollLeft}
                  className={`w-16 h-16 rounded-full border border-navy/10 flex items-center justify-center transition-all duration-500 shadow-sm ${!canScrollLeft ? 'opacity-20 cursor-not-allowed' : 'hover:bg-navy hover:text-white hover:shadow-lg'}`}
                 >
                    <ChevronLeft size={24} />
                 </button>
                 <button 
                  onClick={() => scroll('right')}
                  disabled={!canScrollRight}
                  className={`w-16 h-16 rounded-full border border-navy/10 flex items-center justify-center transition-all duration-500 shadow-sm ${!canScrollRight ? 'opacity-20 cursor-not-allowed' : 'hover:bg-navy hover:text-white hover:shadow-lg'}`}
                 >
                    <ChevronRight size={24} />
                 </button>
              </div>
            </div>
          </div>

          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-8 overflow-x-auto scrollbar-hide pb-12 snap-x snap-mandatory pt-4"
          >
             {reviews.map((review, index) => (
               <div 
                key={index} 
                className="review-reveal min-w-[320px] md:min-w-[450px] p-10 lg:p-12 rounded-[3rem] bg-white border border-navy/5 shadow-glass-light hover:shadow-glass-heavy transition-all duration-700 space-y-10 group cursor-pointer relative snap-center overflow-hidden flex flex-col justify-between"
               >
                 {/* Cinematic Blur */}
                 <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/5 rounded-full blur-[60px] group-hover:bg-primary/10 transition-all duration-1000 -z-10"></div>
                 
                 <div className="space-y-8">
                   <div className="flex justify-between items-start">
                      <div className="bg-primary/10 p-4 rounded-[1.5rem] text-primary transition-transform duration-700 group-hover:-rotate-12">
                         <Quote size={24} fill="currentColor" className="opacity-80" />
                      </div>
                      <div className="flex gap-1.5">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={14} className="text-primary fill-primary opacity-80" />
                        ))}
                      </div>
                   </div>
                   
                   <p className="text-xl lg:text-2xl font-display font-medium leading-relaxed italic text-navy/80">
                     "{review.text}"
                   </p>
                 </div>

                 <div className="pt-10 border-t border-navy/5 flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-4">
                       <div className="w-14 h-14 bg-navy text-white rounded-[1.2rem] flex items-center justify-center font-bold text-xl shadow-lg shadow-navy/20 group-hover:bg-primary group-hover:shadow-primary/20 transition-all duration-500">
                          {review.name.charAt(0)}
                       </div>
                       <div>
                          <p className="font-bold text-navy group-hover:text-primary transition-colors">{review.name}</p>
                          <p className="text-[10px] uppercase font-black tracking-[0.2em] text-navy/30 mt-1">{review.date}</p>
                       </div>
                    </div>
                    <span className="px-4 py-1.5 bg-secondary text-primary border border-primary/20 text-[9px] font-black uppercase tracking-widest rounded-full">{review.source}</span>
                 </div>
               </div>
             ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
